import { create } from "zustand";
import { persist } from "zustand/middleware";

interface AuthState {
  token: string | null;
  setToken: (token: string) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      setToken: (token) => set({ token }),
      logout: () => {
        set({ token: null });
      },
      isAuthenticated: () => get().token !== null,
    }),
    {
      name: "tripmuscle_jwt",
      partialize: (state) => ({ token: state.token }),
    }
  )
);

const AUTH_URL = "https://auth.wade.technology";
const APP_NAME = "tripmuscle";

/**
 * Where to send somebody after they sign in.
 *
 * ⚠️ SANITISED, because it comes off the URL bar and is fed to `navigate()`.
 * Only a same-origin PATH is allowed: it must start with a single "/" and must
 * not start with "//" (which a browser reads as a protocol-relative URL to
 * another host) or contain a scheme. Anything else is discarded in favour of
 * the dashboard rather than turning our login into an open redirect.
 */
export function safeNextPath(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const v = String(raw).trim();
  if (!v.startsWith("/")) return null;
  if (v.startsWith("//")) return null;
  if (v.includes("\\")) return null;
  // A control character or a scheme smuggled in via encoding is not a path.
  if (/[\u0000-\u001f\u007f]/.test(v)) return null;
  if (/^\/+\w+:/.test(v)) return null;
  return v;
}

/**
 * § G29 — sign-in RETURNS YOU TO WHERE YOU WERE GOING.
 *
 * The callback used to navigate unconditionally to /dashboard, which silently
 * ate the destination of every protected deep link. The one that mattered:
 * an attendee clicking `/claim?token=…` from an invitation, signed out, was
 * bounced through login and landed on a dashboard with the token gone — so the
 * claim link simply did not work for the people it was written for, and
 * ClaimTripPage's own comment ("returns here with the token still in the URL")
 * described behaviour that did not exist.
 *
 * The whole path INCLUDING the query is carried, because the token lives there.
 */
export function getLoginUrl(next?: string) {
  const intended =
    safeNextPath(next) ??
    (typeof window !== "undefined"
      ? safeNextPath(window.location.pathname + window.location.search)
      : null);
  const callback =
    window.location.origin +
    "/auth/callback" +
    (intended ? "?next=" + encodeURIComponent(intended) : "");
  return AUTH_URL + "/login?app=" + APP_NAME + "&redirect=" + encodeURIComponent(callback);
}
